import { identity } from 'lodash-es';
import type { Component } from 'vue';
import { HomeIcon, MailIcon, PhoneIcon } from '@glow/shared/components/icons';
import {
  InstagramIcon,
  OdnoklassnikiIcon,
  TelegramIcon,
  VkIcon,
  WhatsappIcon,
  YoutubeIcon,
} from '@glow/shared/components/icons/social';
import {
  emailPattern,
  formatEmailLink,
  formatInstagramLink,
  formatInstagramName,
  formatOdnoklassnikiLink,
  formatOdnoklassnikiName,
  formatPhone,
  formatPhoneLink,
  formatTelegramLink,
  formatTelegramName,
  formatVkName,
  formatVkLink,
  formatWhatsappLink,
  formatYoutubeName,
  formatYoutubeLink,
  formatPersonalSiteLink,
  instagramProfilePattern,
  odnoklassnikiProfilePattern,
  personalSitePattern,
  phonePattern,
  telegramProfilePattern,
  vkProfilePattern,
  youtubeProfilePattern,
} from '@glow/shared/utils/format';
import type { Nullable, OperatingSystem } from '@glow/shared/utils/types';
import type { ContactEntity } from '@/gql/graphql';

export type UserContactFields = {
  instagramProfile?: Nullable<string>;
  telegramProfile?: Nullable<string>;
  whatsappProfile?: Nullable<string>;
  youtubeProfile?: Nullable<string>;
  vkProfile?: Nullable<string>;
  odnoklassnikiProfile?: Nullable<string>;
  personalSite?: Nullable<string>;
  phone?: Nullable<string>;
  email?: Nullable<string>;
};

export type SocialLinkFormatterFn = (
  name: string,
  os?: OperatingSystem
) => string;
export type SocialNameFormatterFn = (name: string) => string;
export type UserContactDescription = {
  field: keyof ContactEntity;
  title: string;
  placeholder: string;
  userField: string;
  validationPattern: RegExp;
  Icon: Component;
  linkFormatter: SocialLinkFormatterFn;
  nameFormatter: SocialNameFormatterFn;
};

export const USER_SOCIAL_DESCRIPTIONS = {
  instagram: {
    field: 'instagramProfile',
    title: 'Instagram',
    placeholder: '@',
    userField: 'instagramProfile',
    validationPattern: instagramProfilePattern,
    Icon: InstagramIcon,
    linkFormatter: formatInstagramLink,
    nameFormatter: formatInstagramName,
  } as UserContactDescription,
  telegram: {
    field: 'telegramProfile',
    title: 'Telegram',
    placeholder: '@',
    userField: 'telegramProfile',
    validationPattern: telegramProfilePattern,
    Icon: TelegramIcon,
    linkFormatter: formatTelegramLink,
    nameFormatter: formatTelegramName,
  } as UserContactDescription,
  whatsapp: {
    field: 'whatsappProfile',
    title: 'WhatsApp',
    placeholder: '+7',
    userField: 'whatsappProfile',
    validationPattern: phonePattern,
    Icon: WhatsappIcon,
    linkFormatter: formatWhatsappLink,
    nameFormatter: formatPhone,
  } as UserContactDescription,
  youtube: {
    field: 'youtubeProfile',
    title: 'YouTube',
    placeholder: 'https://youtube.com/',
    userField: 'youtubeProfile',
    validationPattern: youtubeProfilePattern,
    Icon: YoutubeIcon,
    linkFormatter: formatYoutubeLink,
    nameFormatter: formatYoutubeName,
  } as UserContactDescription,
  vk: {
    field: 'vkProfile',
    title: 'VK',
    placeholder: 'id профиля',
    userField: 'vkProfile',
    validationPattern: vkProfilePattern,
    Icon: VkIcon,
    linkFormatter: formatVkLink,
    nameFormatter: formatVkName,
  } as UserContactDescription,
  odnoklassniki: {
    field: 'odnoklassnikiProfile',
    title: 'Одноклассники',
    placeholder: 'id профиля',
    userField: 'odnoklassnikiProfile',
    validationPattern: odnoklassnikiProfilePattern,
    Icon: OdnoklassnikiIcon,
    linkFormatter: formatOdnoklassnikiLink,
    nameFormatter: formatOdnoklassnikiName,
  } as UserContactDescription,
  site: {
    field: 'personalSite',
    title: 'Личный сайт',
    placeholder: 'example.com',
    userField: 'personalSite',
    validationPattern: personalSitePattern,
    Icon: HomeIcon,
    linkFormatter: formatPersonalSiteLink,
    nameFormatter: identity,
  } as UserContactDescription,
};
export type UserSocial = keyof typeof USER_SOCIAL_DESCRIPTIONS;
export const USER_SOCIALS = Object.keys(
  USER_SOCIAL_DESCRIPTIONS
) as UserSocial[];

export const USER_CONTACT_DESCRIPTIONS = {
  phone: {
    field: 'phone',
    title: 'Номер телефона',
    placeholder: '+7',
    userField: 'phone',
    validationPattern: phonePattern,
    Icon: PhoneIcon,
    linkFormatter: formatPhoneLink,
    nameFormatter: formatPhone,
  } as UserContactDescription,
  email: {
    field: 'email',
    title: 'Электронный адрес',
    placeholder: 'email@example.com',
    userField: 'email',
    validationPattern: emailPattern,
    Icon: MailIcon,
    linkFormatter: formatEmailLink,
    nameFormatter: identity,
  } as UserContactDescription,
  ...USER_SOCIAL_DESCRIPTIONS,
};
export type UserContact = keyof typeof USER_CONTACT_DESCRIPTIONS;
export const USER_CONTACTS = Object.keys(
  USER_CONTACT_DESCRIPTIONS
) as UserContact[];
